import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useAuthKeyReady } from '../../shared/context/AuthKeyContext';
import {
  useGetDashboardBirthdayQuery,
  useGetInboxQuery,
  useClickResponseMutation,
  useSaveResponseMutation,
  useGetRteSeatsQuery,
  useGetMovieScreeningQuery,
  useAddMovieScreeningLogMutation,
  useLazyGetUploadedFilesQuery,
} from './Dashboard.service';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import './dashboard.css';
import arrowIcon from '../../assets/icons/Icon awesome-arrow-circle-right.svg';
import boysIcon1 from '../../assets/icons/Group 34938.svg';
import girlsIcon1 from '../../assets/icons/icons8-student.svg';
import boysIcon2 from '../../assets/icons/Group 35070.svg';
import girlsIcon2 from '../../assets/icons/woman.svg';
import mailIcon1 from '../../assets/icons/open-mail.svg';
import mailIcon2 from '../../assets/icons/_x31_4_Email_notification.svg';
import arrowLeft from '../../assets/icons/arrow-left.svg';
import { toast } from 'react-toastify';
// ─── Types ────────────────────────────────────────────────────────────────────

interface BirthdayPerson {
  emis_id: string;
  name: string;
  details: string;
  type: string;
}

interface MailItem {
  IndxId: string;
  FrmUsrName: string;
  Title: string;
  Msg: string;
  DueDate: string;
  ViewStatus: string;
  Attach1?: string;
  Attach2?: string;
  Attach3?: string;
  ResponseRequire?: string;
  TaskCompleted?: string;
}

// ─── Helper: Simple Accordion ─────────────────────────────────────────────────

interface AccordionTabProps {
  header: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  /** Controlled mode: parent tells this tab whether it's open */
  isOpen?: boolean;
  /** Controlled mode: parent handles toggle */
  onToggle?: () => void;
}

const AccordionTab: React.FC<AccordionTabProps> = ({
  header,
  children,
  defaultOpen = false,
  isOpen,
  onToggle,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  // If isOpen prop is provided, use controlled mode; otherwise self-managed
  const open = isOpen !== undefined ? isOpen : internalOpen;
  const handleClick = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalOpen(!internalOpen);
    }
  };

  return (
    <div className="accordion-item">
      <div className="accordion-header" onClick={handleClick}>
        <div>{header}</div>
        <span className={`accordion-chevron ${open ? 'open' : ''}`}>▼</span>
      </div>
      {open && <div className="accordion-body">{children}</div>}
    </div>
  );
};

// ─── Helper: Simple Paginated Table ───────────────────────────────────────────

// ─── Helper: Modal ────────────────────────────────────────────────────────────

interface ModalProps {
  visible: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  visible,
  title,
  onClose,
  children,
  footer,
}) => {
  if (!visible) return null;
  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog-custom">
        <div className="modal-header-custom">
          <h5>{title}</h5>
          <button className="btn-close-modal" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body-custom">{children}</div>
        {footer && <div className="modal-footer-custom">{footer}</div>}
      </div>
    </div>
  );
};

// ─── Helper: format date ──────────────────────────────────────────────────────

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB'); // dd/MM/yyyy
  } catch {
    return dateStr;
  }
};

// ─── Main Dashboard Component ─────────────────────────────────────────────────

type RightView = 'dashboard' | 'student' | 'teacher' | 'inbox' | 'inboxMsg';

const Dashboard: React.FC = () => {
  const s = useSelector((state: RootState) => state.session);
  const isAuthKeyReady = useAuthKeyReady();
  const [triggerGetUploadedFiles] = useLazyGetUploadedFilesQuery();
  // ── Session shortcuts ──
  const schoolId = s.schoolId ?? '';
  const username = s.username ?? '';
  const userId = s.userId ?? '';
  const districtId = s.districtId ?? '';
  const blockId = s.blockId ?? '';
  const schoolTypeId = s.schoolTypeId ?? '';
  const cateId = s.cate_id ?? '';
  const manageId = s.manage_id ?? '';
  const usertypeid = s.usertypeid ?? '';
  const emisUsertype1 = s.emisUsertype1 ?? '';

  // ── UI State ──
  const [rightView, setRightView] = useState<RightView>('student');
  const [stuToggle, setStuToggle] = useState(true);

  // Track which accordion tab is open (only one at a time) in student/teacher panels
  const [openStudentTab, setOpenStudentTab] = useState<string | null>(null);
  const [openTeacherTab, setOpenTeacherTab] = useState<string | null>(null);
  const [teacherToggle, setTeacherToggle] = useState(false);
  const [inboxToggle, setInboxToggle] = useState(false);
  const [displayModal, setDisplayModal] = useState(false);
  const [eventModal, setEventModal] = useState(false);
  const [indexId, setIndexId] = useState('');

  // ── Top cards state ──
  const [monthNme, setMonthNme] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [todaySpl, setTodaySpl] = useState('');
  const [todayQuotes, setTodayQuotes] = useState('');
  const [todayQuotesAuthor, setTodayQuotesAuthor] = useState('');

  // ── Dashboard data state ──
  const [overAllStudents, setOverAllStudents] = useState<any>(0);
  const [overAllStudentsTotalBoys, setOverAllStudentsTotalBoys] =
    useState<any>(0);
  const [overAllStudentsTotalGirls, setOverAllStudentsTotalGirls] =
    useState<any>(0);
  const [overAllStaffTotal, setOverAllStaffTotal] = useState<any>(0);
  const [overAllStaffTotalGents, setOverAllStaffTotalGents] = useState<any>(0);
  const [overAllStaffTotalLadies, setOverAllStaffTotalLadies] =
    useState<any>(0);
  const [studentCountList, setStudentCountList] = useState<any[]>([]);
  const [teacherTypeCountList, setTeacherTypeCountList] = useState<any[]>([]);
  const [teacherDesignationCountList, setTeacherDesignationCountList] =
    useState<any[]>([]);
  const [stdCoummunity, setStdCoummunity] = useState<any[]>([]);

  // ── Inbox state ──
  const [tablelist, setTablelist] = useState<MailItem[]>([]);
  const [mailCount, setMailCount] = useState(0);
  const [readMailCount, setReadMailCount] = useState(0);
  const [unReadMailCount, setUnReadMailCount] = useState(0);
  const [mailSubject, setMailSubject] = useState('');
  const [mailSender, setMailSender] = useState('');
  const [mailContent, setMailContent] = useState('');
  const [mailReceiveDate, setMailReceiveDate] = useState('');
  const [attachment1, setAttachment1] = useState('');
  const [attachment2, setAttachment2] = useState('');
  const [attachment3, setAttachment3] = useState('');
  const [showResponseRequire, setShowResponseRequire] = useState('');
  const [taskCompleted, setTaskCompleted] = useState('2');

  // ── Birthday state ──
  const [teacherBirthdayList, setTeacherBirthdayList] = useState<
    BirthdayPerson[]
  >([]);
  const [stuBirthdayList, setStuBirthdayList] = useState<BirthdayPerson[]>([]);
  const [teacherBirthCount, setTeacherBirthCount] = useState(0);
  const [stuBirthCount, setStuBirthCount] = useState(0);

  // ── RTE state ──
  const [rteStatus, setRteStatus] = useState(false);
  const [retClass, setRetClass] = useState('');
  const [rteSeatCnt, setRteSeatCnt] = useState(0);

  // ── Movie screening state ──
  const [showScreeningMovieCard, setShowScreeningMovieCard] = useState(false);
  const [currentMovie, setCurrentMovie] = useState<any>(null);
  const [screeningMovieName, setScreeningMovieName] = useState('');
  const [screeningMovieId, setScreeningMovieId] = useState<number>(0);

  // ── Add event form state ──
  const [eventVenue, setEventVenue] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [eventConductedBy, setEventConductedBy] = useState('');
  const [eventTopics, setEventTopics] = useState('');

  // ── RTK Query hooks ──
  const { data: birthdayData } = useGetDashboardBirthdayQuery(
    { schoolId },
    { skip: !s.loaded || !schoolId || !isAuthKeyReady }
  );

  const { data: inboxData, refetch: refetchInbox } = useGetInboxQuery(
    { districtId, blockId, schoolTypeId, manageId: manageId, cateId },
    {
      skip:
        !s.loaded ||
        !isAuthKeyReady ||
        !districtId ||
        !blockId ||
        !schoolTypeId ||
        !manageId ||
        !cateId,
    }
  );

  const { data: rteData } = useGetRteSeatsQuery(
    { schoolId },
    { skip: !s.loaded || !isAuthKeyReady || schoolTypeId != 3 }
  );

  const { data: movieData } = useGetMovieScreeningQuery(undefined, {
    skip: !s.loaded || !isAuthKeyReady || schoolTypeId !== 1,
  });

  const [clickResponseMutation] = useClickResponseMutation();
  const [saveResponseMutation] = useSaveResponseMutation();
  const [addMovieScreeningLog] = useAddMovieScreeningLogMutation();

  // ── Effects ──

  // Date & month
  useEffect(() => {
    const day = new Date();
    const date = String(day.getDate()).padStart(2, '0');
    const monthName = day
      .toLocaleString('default', { month: 'short' })
      .toUpperCase();
    setTimeout(() => {
      setDateStr(date);
      setMonthNme(monthName);
    }, 0);
  }, []);

  // Special day
  useEffect(() => {
    fetch('/assets/json/today_special.json')
      .then((r) => r.json())
      .then((res: any[]) => {
        const today = new Date();
        const dd = String(today.getDate()).padStart(2, '0');
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const yyyy = today.getFullYear();
        const dateKey = `${dd}-${mm}-${yyyy}`;
        const found = res.find((item) => item.Date === dateKey);
        if (found) setTodaySpl(found.Special);
      })
      .catch(() => {});
  }, []);

  // Today's quotes
  useEffect(() => {
    fetch('/assets/json/Quotes.json')
      .then((r) => r.json())
      .then((res: any[]) => {
        const today = new Date();
        const dd = String(today.getDate()).padStart(2, '0');
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        res.forEach((item) => {
          const parts = item.Date?.split('-') ?? [];
          if (parts[0] === dd && parts[1] === mm) {
            setTodayQuotes(item.Quote);
            setTodayQuotesAuthor(item.Written_by);
          }
        });
      })
      .catch(() => {});
  }, []);

  // Dashboard JSON (S3)
  useEffect(() => {
    if (!schoolId) return;
    const url = `https://d1wpyxz35bzzz4.cloudfront.net/school_student_staff_count_details_${schoolId}.json`;
    fetch(url)
      .then((r) => r.json())
      .then((res: any) => {
        const communityList = res.student_castwise?.[0] ?? {};
        const staffDetails = res.staff_details?.[0] ?? {};

        setStdCoummunity([
          { communtiy: 'BC', total: communityList['bc'] || '0' },
          { communtiy: 'DNC', total: communityList['dnc'] || '0' },
          { communtiy: 'MBC', total: communityList['mbc'] || '0' },
          { communtiy: 'OC', total: communityList['oc'] || '0' },
          { communtiy: 'SC', total: communityList['sc'] || '0' },
          { communtiy: 'ST', total: communityList['st'] || '0' },
        ]);

        setOverAllStudents(res.students_count?.[0]?.['total'] ?? 0);
        setOverAllStudentsTotalBoys(
          res.students_count?.[0]?.['total_boys'] ?? 0
        );
        setOverAllStudentsTotalGirls(
          res.students_count?.[0]?.['total_girls'] ?? 0
        );

        const staffTotal =
          (staffDetails['Teaching_male'] ?? 0) +
          (staffDetails['Teaching_female'] ?? 0) +
          (staffDetails['nonteach_male'] ?? 0) +
          (staffDetails['nonteach_female'] ?? 0);
        setOverAllStaffTotal(staffTotal);
        setOverAllStaffTotalGents(
          (staffDetails['Teaching_male'] ?? 0) +
            (staffDetails['nonteach_male'] ?? 0)
        );
        setOverAllStaffTotalLadies(
          (staffDetails['Teaching_female'] ?? 0) +
            (staffDetails['nonteach_female'] ?? 0)
        );

        setTeacherTypeCountList([
          {
            body_type: 'Teaching',
            male: staffDetails['Teaching_male'],
            female: staffDetails['Teaching_female'],
            total:
              (staffDetails['Teaching_male'] ?? 0) +
              (staffDetails['Teaching_female'] ?? 0),
          },
          {
            body_type: 'Non Teaching',
            male: staffDetails['nonteach_male'],
            female: staffDetails['nonteach_female'],
            total:
              (staffDetails['nonteach_male'] ?? 0) +
              (staffDetails['nonteach_female'] ?? 0),
          },
        ]);

        setTeacherDesignationCountList([
          {
            body_type: 'HM',
            male: staffDetails['HM_male'],
            female: staffDetails['HM_female'],
            total:
              (staffDetails['HM_male'] ?? 0) + (staffDetails['HM_female'] ?? 0),
          },
          {
            body_type: 'PG',
            male: staffDetails['PG_male'],
            female: staffDetails['PG_female'],
            total:
              (staffDetails['PG_male'] ?? 0) + (staffDetails['PG_female'] ?? 0),
          },
          {
            body_type: 'BT',
            male: staffDetails['BT_male'],
            female: staffDetails['BT_female'],
            total:
              (staffDetails['BT_male'] ?? 0) + (staffDetails['BT_female'] ?? 0),
          },
          {
            body_type: 'SG',
            male: staffDetails['SG_male'],
            female: staffDetails['SG_female'],
            total:
              (staffDetails['SG_male'] ?? 0) + (staffDetails['SG_female'] ?? 0),
          },
          {
            body_type: 'SPT',
            male: staffDetails['SPT_male'],
            female: staffDetails['SPT_female'],
            total:
              (staffDetails['SPT_male'] ?? 0) +
              (staffDetails['SPT_female'] ?? 0),
          },
          {
            body_type: 'PET',
            male: staffDetails['PET_male'],
            female: staffDetails['pet_female'],
            total:
              (staffDetails['PET_male'] ?? 0) +
              (staffDetails['pet_female'] ?? 0),
          },
          {
            body_type: 'PD',
            male: staffDetails['PD_male'],
            female: staffDetails['PD_female'],
            total:
              (staffDetails['PD_male'] ?? 0) + (staffDetails['PD_female'] ?? 0),
          },
          {
            body_type: 'VOC',
            male: staffDetails['VOC_male'],
            female: staffDetails['VOC_female'],
            total:
              (staffDetails['VOC_male'] ?? 0) +
              (staffDetails['VOC_female'] ?? 0),
          },
          {
            body_type: 'COMP',
            male: staffDetails['COMP_male'],
            female: staffDetails['COMP_female'],
            total:
              (staffDetails['COMP_male'] ?? 0) +
              (staffDetails['COMP_female'] ?? 0),
          },
          {
            body_type: 'Others',
            male: staffDetails['Others_male'],
            female: staffDetails['Others_female'],
            total:
              (staffDetails['Others_male'] ?? 0) +
              (staffDetails['Others_female'] ?? 0),
          },
        ]);

        setStudentCountList(res.classwise_count ?? []);
      })
      .catch(() => {});
  }, [schoolId]);

  // Birthday data
  useEffect(() => {
    if (!birthdayData?.dataStatus || birthdayData.message == 'NO data Found')
      return;
    const teachers: BirthdayPerson[] = [];
    const students: BirthdayPerson[] = [];
    birthdayData.result.forEach((item: BirthdayPerson) => {
      if (item.type === '1') teachers.push(item);
      else if (item.type === '2') students.push(item);
    });
    setTimeout(() => {
      setTeacherBirthdayList(teachers);
      setStuBirthdayList(students);
      setTeacherBirthCount(teachers.length);
      setStuBirthCount(students.length);
    }, 0);
  }, [birthdayData]);

  // Inbox data
  useEffect(() => {
    if (!inboxData?.result) return;
    const list: MailItem[] = [...inboxData.result];
    list.sort(
      (a, b) => new Date(b.DueDate).getTime() - new Date(a.DueDate).getTime()
    );
    let read = 0,
      unread = 0;
    list.forEach((x) => {
      x.ViewStatus === '2' ? read++ : unread++;
    });
    console.log('listed_items', list);
    setTimeout(() => {
      setTablelist(list);
      setMailCount(list.length);
      setReadMailCount(read);
      setUnReadMailCount(unread);
    }, 0);
  }, [inboxData]);

  // RTE seats
  useEffect(() => {
    if (!rteData) return;
    if (rteData.dataStatus) {
      setTimeout(() => {
        setRteStatus(true);
        setRetClass(rteData.result?.[0]?.Class ?? '');
        setRteSeatCnt(rteData.result?.[0]?.Seats ?? 0);
      }, 0);
    } else {
      setTimeout(() => setRteStatus(false), 0);
    }
  }, [rteData]);

  // Movie screening
  useEffect(() => {
    console.log(movieData, cateId, schoolTypeId);
    if (!movieData?.dataStatus || !movieData.result?.length) {
      setTimeout(() => setShowScreeningMovieCard(false), 0);
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eligible = movieData.result.filter((movie: any) => {
      try {
        if (movie.curr_status !== '1') return false;
        const start = new Date(movie.start_date);
        start.setHours(0, 0, 0, 0);
        const end = new Date(movie.end_date);
        end.setHours(0, 0, 0, 0);
        if (today < start || today > end) return false;
        const appTypes = (movie.app_Type?.toString() || '')
          .split(',')
          .map((t: string) => t.trim());
        if (!appTypes.includes('1')) return false;
        const cats = (movie.category?.toString() || '')
          .split(',')
          .map((c: string) => c.trim())
          .filter((c: string) => c && c !== '[]');
        if (cats.length > 0 && !cats.includes(cateId)) return false;
        const schlTypes = (movie.school_Type?.toString() || '')
          .split(',')
          .map((s: string) => s.trim())
          .filter((s: string) => s && s !== '[]');
        if (schlTypes.length > 0 && !schlTypes.includes(schoolTypeId))
          return false;
        return true;
      } catch {
        return false;
      }
    });
    console.log('eligible', eligible);
    if (eligible.length === 0) {
      setTimeout(() => setShowScreeningMovieCard(false), 0);
      return;
    }
    eligible.sort(
      (a: any, b: any) =>
        new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
    );
    const movie = eligible[0];
    let movieName = movie.movie_title;
    let movieId = movie.id;
    try {
      const details = JSON.parse(movie.pre_movie_link);
      const primary = details?.find((m: any) => m.is_primary);
      movieName = primary?.movie_title ?? movie.movie_title;
      movieId = movie.id;
    } catch {
      // noop
    }
    setTimeout(() => {
      setCurrentMovie(movie);
      setShowScreeningMovieCard(true);
      setScreeningMovieName(movieName);
      setScreeningMovieId(movieId);
    }, 0);
  }, [movieData, cateId, schoolTypeId]);

  // ── Navigation helpers ──
  const showStudentDashboard = () => {
    setRightView('student');
    setStuToggle(true);
    setTeacherToggle(false);
    setInboxToggle(false);
  };
  const showTeacherDashboard = () => {
    setRightView('teacher');
    setStuToggle(false);
    setTeacherToggle(true);
    setInboxToggle(false);
  };
  const showInboxDashboard = () => {
    setRightView('inbox');
    setStuToggle(false);
    setTeacherToggle(false);
    setInboxToggle(true);
  };
  const showEventsDashboard = () => {
    setRightView('dashboard');
    setStuToggle(true);
    setTeacherToggle(false);
    setInboxToggle(false);
  };

  // ── Inbox message handler ──
  const inboxMsg = useCallback(
    async (item: MailItem) => {
      setRightView('inboxMsg');
      setMailContent(item.Msg);
      setMailSender(item.FrmUsrName);
      setMailSubject(item.Title);
      setMailReceiveDate(item.DueDate);
      setAttachment1(item.Attach1 ?? '');
      setAttachment2(item.Attach2 ?? '');
      setAttachment3(item.Attach3 ?? '');
      setShowResponseRequire(item.ResponseRequire ?? '');
      setTaskCompleted(item.TaskCompleted ?? '2');

      if (!item.ViewStatus) {
        setReadMailCount((c) => c + 1);
        setUnReadMailCount((c) => c - 1);
      }

      const payload = {
        records: {
          IndxId: '',
          CommsId: item.IndxId,
          ToUsrType: usertypeid,
          ToUsrType1: emisUsertype1,
          ToUsrId: userId,
          ToUsrName: username,
          ViewStatus: '2',
        },
      };
      try {
        const res = await clickResponseMutation(payload).unwrap();
        setIndexId(res.IndexId);
        toast.success('Message Viewed');
        refetchInbox();
      } catch {
        /* noop */
      }
    },
    [
      clickResponseMutation,
      refetchInbox,
      usertypeid,
      emisUsertype1,
      userId,
      username,
    ]
  );

  // ── Save task response ──
  const saveResponse = async () => {
    const payload = {
      records: { IndxId: indexId, TaskCompleted: taskCompleted },
    };
    try {
      await saveResponseMutation(payload).unwrap();
      setRightView('inbox');
    } catch {
      /* noop */
    }
  };

  // ── Attachment viewer ──
  const showAttachments = async (which: '1' | '2' | '3') => {
    const filename =
      which === '1' ? attachment1 : which === '2' ? attachment2 : attachment3;
    if (!filename) return;

    try {
      const result = await triggerGetUploadedFiles({
        bucket_name: 'renewalapplicationemis',
        file_name: filename,
        expiry: 1800,
      }).unwrap(); // .unwrap() gives you the data directly or throws on error

      if (result?.url) window.open(result.url);
    } catch {
      /* noop */
    }
  };

  // ── Open children film ──
  const openChildrenFilm = async () => {
    if (!currentMovie) return;
    const payload = {
      records: { user_id: username, movie_id: screeningMovieId, app_type: 1 },
    };
    try {
      const res = await addMovieScreeningLog(payload).unwrap();
      if (res.dataStatus) {
        window.location.href = 'https://ddx6ukbne05nx.cloudfront.net/dashboard';
      }
    } catch {
      /* noop */
    }
  };

  // ── Top cards column class ──
  const topCardColClass = showScreeningMovieCard
    ? 'col-sm-12 col-md-6 col-lg-6 col-xl-3 grid-margin stretch-card'
    : 'col-sm-12 col-md-6 col-lg-6 col-xl-4 grid-margin stretch-card';

  if (!s.loaded) {
    return (
      <main className="app-content p-3">
        <p className="text-muted">Loading session…</p>
      </main>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <main className="app-content p-3">
      {/* ── Top 4 Info Cards ───────────────────────────────────────────────── */}
      <div className="row mb-3">
        {/* Today's Special */}
        <div className={`${topCardColClass} stretch-card`}>
          <div className="card dashboard-card">
            <div className="card-body">
              <div className="row">
                <div
                  className="col-md-3 mt-2 cursor-pointer"
                  onClick={showEventsDashboard}
                >
                  <div className="dashCard text-center">
                    <span>{monthNme}</span>
                    <br />
                    <span className="font-size-large">{dateStr}</span>
                  </div>
                </div>
                <div className="col-md-9 mt-2">
                  <span className="cardTitle">Today&apos;s Special</span>
                  <br />
                  <span className="bold">{todaySpl}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Birthday */}
        <div className={`${topCardColClass} stretch-card`}>
          <div className="card dashboard-card">
            <div
              className="card-body cursor-pointer"
              onClick={() => setDisplayModal(true)}
            >
              <div className="row">
                <div className="col-10">
                  <span className="cardTitle">Today&apos;s Birthday</span>
                  <br />
                  <span className="bold">
                    Teachers - <b>{teacherBirthCount}</b>
                  </span>
                  <span className="bold vertical-line" />
                  <span className="bold">
                    Students - <b>{stuBirthCount}</b>
                  </span>
                </div>
                <div className="col-2 mt-2">
                  <i className="pi pi-info-circle" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Quotes */}
        <div className={`${topCardColClass} stretch-card`}>
          <div className="card dashboard-card">
            <div className="card-body">
              <div className="row">
                <div className="col-12">
                  <span className="cardTitle">Today&apos;s Quotes</span>
                  <br />
                  <span className="bold">{todayQuotes}</span>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-12 text-end">
                  <span className="bold">- {todayQuotesAuthor}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Movie Screening (conditional) */}
        {showScreeningMovieCard && (
          <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3 grid-margin stretch-card">
            <div className="card dashboard-card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className="cardTitle fw-bold">
                      Children Movie Screening
                    </span>
                    <br />
                    <span className="badge-new">New</span>
                  </div>
                  <button className="btn-view-movie" onClick={openChildrenFilm}>
                    View
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Main Dashboard Row ─────────────────────────────────────────────── */}
      <div className="row">
        {/* ── Left Panel: Student / Staff / Inbox counts ──────────────────── */}
        <div className="col-lg-5 col-xl-4 grid-margin grid-margin-xl-0 stretch-card">
          <div className="card dashboard-card allDashboard">
            <div className="card-body">
              {/* Students */}
              <div className="row my-2">
                <div className="col-10 py-2">
                  <div className="row stdRow">
                    <div className="col-9">
                      <p>TOTAL STUDENTS</p>
                    </div>
                    <div className="col-3">
                      <p className="text-end">{overAllStudents}</p>
                    </div>
                    <div className="col-8 mt-2">
                      <div className="row">
                        <div className="col-2">
                          <img src={boysIcon1} alt="boys" />
                        </div>
                        <div className="col-8">
                          <p>Boys</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-4 mt-2">
                      <p className="text-end">{overAllStudentsTotalBoys}</p>
                    </div>
                    <div className="col-8 mt-2">
                      <div className="row">
                        <div className="col-2">
                          <img src={girlsIcon1} alt="girls" />
                        </div>
                        <div className="col-8">
                          <p>Girls</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-4 mt-2">
                      <p className="text-end">{overAllStudentsTotalGirls}</p>
                    </div>
                  </div>
                </div>
                <div className="col-2 py-2">
                  <button
                    id="studentButton"
                    className={`btn-toggle ${stuToggle ? 'blue' : 'brown'}`}
                    onClick={showStudentDashboard}
                  >
                    <img src={arrowIcon} alt="arrow" />
                  </button>
                </div>
                <hr />
              </div>

              {/* Staff */}
              <div className="row my-2">
                <div className="col-10 py-2">
                  <div className="row staffRow">
                    <div className="col-8">
                      <p>TOTAL STAFF</p>
                    </div>
                    <div className="col-4">
                      <p className="text-end">{overAllStaffTotal}</p>
                    </div>
                    <div className="col-8 mt-2">
                      <div className="row">
                        <div className="col-2">
                          <img src={boysIcon2} alt="male" />
                        </div>
                        <div className="col-8">
                          <p>Male</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-4 mt-2">
                      <p className="text-end">{overAllStaffTotalGents}</p>
                    </div>
                    <div className="col-8 mt-2">
                      <div className="row">
                        <div className="col-2">
                          <img src={girlsIcon2} alt="female" />
                        </div>
                        <div className="col-8">
                          <p>Female</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-4 mt-2">
                      <p className="text-end">{overAllStaffTotalLadies}</p>
                    </div>
                  </div>
                </div>
                <div className="col-2 py-2">
                  <button
                    id="staffButton"
                    className={`btn-toggle ${teacherToggle ? 'green' : 'brown'}`}
                    onClick={showTeacherDashboard}
                  >
                    <img src={arrowIcon} alt="arrow" />
                  </button>
                </div>
                <hr />
              </div>

              {/* Inbox */}
              <div className="row my-2">
                <div className="col-10 py-2">
                  <div className="row inboxRow">
                    <div className="col-8">
                      <p>INBOX</p>
                    </div>
                    <div className="col-4">
                      <p className="text-end">{mailCount}</p>
                    </div>
                    <div className="col-8 mt-2">
                      <div className="row">
                        <div className="col-2">
                          <img src={mailIcon1} alt="read" />
                        </div>
                        <div className="col-8">
                          <p>Read</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-4 mt-2">
                      <p className="text-end">{readMailCount}</p>
                    </div>
                    <div className="col-8 mt-2">
                      <div className="row">
                        <div className="col-2">
                          <img src={mailIcon2} alt="unread" />
                        </div>
                        <div className="col-8">
                          <p>Unread</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-4 mt-2">
                      <p className="text-end">{unReadMailCount}</p>
                    </div>
                  </div>
                </div>
                <div className="col-2 py-2">
                  <button
                    id="inboxButton"
                    className={`btn-toggle ${inboxToggle ? 'pink' : 'brown'}`}
                    onClick={showInboxDashboard}
                  >
                    <img src={arrowIcon} alt="arrow" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Panel ─────────────────────────────────────────────────── */}
        <div className="col-lg-7 col-xl-8 stretch-card">
          {/* Events Dashboard */}
          {rightView === 'dashboard' && (
            <div className="card dashboard-card dashboardCardClass">
              <div className="card-body">
                <div className="row mb-2">
                  <div className="col-6">
                    <h6>Today&apos;s Event</h6>
                  </div>
                  <div className="col-6 text-end">
                    <button
                      className="btn btn-outline-secondary btn-sm rounded-pill"
                      onClick={() => setEventModal(true)}
                    >
                      + Add
                    </button>
                  </div>
                </div>
                <AccordionTab
                  defaultOpen={true}
                  header={
                    <ul
                      style={{ listStyle: 'none', padding: 0, margin: 0 }}
                      className="mt-2"
                    >
                      <li className="date-style">08-07-2022</li>
                      <li>Design Thinking Workshop</li>
                    </ul>
                  }
                >
                  <div className="row">
                    <div className="col-md-6 col-sm-12">
                      {[
                        ['Venue', 'EMIS'],
                        ['Time', '10.00AM-1.00PM'],
                        ['Conducted by', 'kanna'],
                      ].map(([label, val]) => (
                        <div className="row" key={label}>
                          <div className="col-6">
                            <span className="headLine">{label}</span>
                          </div>
                          <div className="col-6">
                            <span>{val}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="col-md-6 col-sm-12">
                      <div className="row">
                        <div className="col-6">
                          <span className="headLine">Topics Covered</span>
                        </div>
                        <div className="col-6">
                          <span>UI Design</span>
                          <br />
                          <span>Frontend Develop</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionTab>

                <br />
                <h6>Upcoming Events</h6>
                <br />
                {[1, 2].map((n) => (
                  <AccordionTab
                    key={n}
                    defaultOpen={false}
                    header={
                      <ul
                        style={{ listStyle: 'none', padding: 0, margin: 0 }}
                        className="mt-2"
                      >
                        <li className="date-style">10-07-2022</li>
                        <li>Design Thinking Workshop</li>
                      </ul>
                    }
                  >
                    <div className="row">
                      <div className="col-md-6 col-sm-12">
                        {[
                          ['Venue', 'EMIS'],
                          ['Time', '10.00AM-1.00PM'],
                          ['Conducted by', 'kanna'],
                        ].map(([label, val]) => (
                          <div className="row" key={label}>
                            <div className="col-6">
                              <span className="headLine">{label}</span>
                            </div>
                            <div className="col-6">
                              <span>{val}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="col-md-6 col-sm-12">
                        <div className="row">
                          <div className="col-6">
                            <span className="headLine">Topics Covered</span>
                          </div>
                          <div className="col-6">
                            <span>UI Design</span>
                            <br />
                            <span>Frontend Develop</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionTab>
                ))}
              </div>
            </div>
          )}

          {/* Student Panel */}
          {rightView === 'student' && (
            <div className="card dashboard-card studentTableClass">
              <div className="card-body">
                <h6 className="mb-3">STUDENTS</h6>
                {schoolTypeId == 3 && (
                  <AccordionTab
                    header="RTE Seats 2026-27"
                    isOpen={openStudentTab === 'rte'}
                    onToggle={() =>
                      setOpenStudentTab(openStudentTab === 'rte' ? null : 'rte')
                    }
                  >
                    <p className="rte-info">
                      {rteStatus
                        ? `Number of RTE seats for academic year 2026-27 in your school is ${rteSeatCnt} in ${retClass}`
                        : 'Your school is not eligible for RTE admissions in academic year 2026-27'}
                    </p>
                  </AccordionTab>
                )}
                <AccordionTab
                  header="By Class"
                  isOpen={openStudentTab === 'class'}
                  onToggle={() =>
                    setOpenStudentTab(
                      openStudentTab === 'class' ? null : 'class'
                    )
                  }
                >
                  <DataTable
                    value={studentCountList}
                    paginator
                    rows={5}
                    sortMode="single"
                    size="small"
                    stripedRows
                    emptyMessage="No data"
                  >
                    <Column field="body_type" header="Class" sortable />
                    <Column field="section" header="Section" sortable />
                    <Column field="boys" header="Boys" sortable />
                    <Column field="girls" header="Girls" sortable />
                    <Column field="total" header="Total" sortable />
                  </DataTable>
                </AccordionTab>
                <AccordionTab
                  header="By Community"
                  isOpen={openStudentTab === 'community'}
                  onToggle={() =>
                    setOpenStudentTab(
                      openStudentTab === 'community' ? null : 'community'
                    )
                  }
                >
                  <DataTable
                    value={stdCoummunity}
                    paginator
                    rows={10}
                    sortMode="single"
                    size="small"
                    stripedRows
                    emptyMessage="No data"
                  >
                    <Column field="communtiy" header="Community" sortable />
                    <Column field="total" header="Total" sortable />
                  </DataTable>
                </AccordionTab>
              </div>
            </div>
          )}

          {/* Teacher Panel */}
          {rightView === 'teacher' && (
            <div className="card dashboard-card teacherTableClass">
              <div className="card-body">
                <h6 className="mb-3">STAFF</h6>
                <AccordionTab
                  header="By Type"
                  isOpen={openTeacherTab === 'type'}
                  onToggle={() =>
                    setOpenTeacherTab(openTeacherTab === 'type' ? null : 'type')
                  }
                >
                  <DataTable
                    value={teacherTypeCountList}
                    paginator
                    rows={10}
                    sortMode="single"
                    size="small"
                    stripedRows
                    emptyMessage="No data"
                  >
                    <Column field="body_type" header="Type" sortable />
                    <Column field="male" header="Male" sortable />
                    <Column field="female" header="Female" sortable />
                    <Column field="total" header="Total" sortable />
                  </DataTable>
                </AccordionTab>
                <AccordionTab
                  header="By Designation"
                  isOpen={openTeacherTab === 'designation'}
                  onToggle={() =>
                    setOpenTeacherTab(
                      openTeacherTab === 'designation' ? null : 'designation'
                    )
                  }
                >
                  <DataTable
                    value={teacherDesignationCountList}
                    paginator
                    rows={5}
                    sortMode="single"
                    size="small"
                    stripedRows
                    emptyMessage="No data"
                  >
                    <Column field="body_type" header="Designation" sortable />
                    <Column field="male" header="Male" sortable />
                    <Column field="female" header="Female" sortable />
                    <Column field="total" header="Total" sortable />
                  </DataTable>
                </AccordionTab>
              </div>
            </div>
          )}

          {/* Inbox List Panel */}
          {rightView === 'inbox' && (
            <div className="card dashboard-card inboxTableClass">
              <div className="card-body">
                <h6 className="mb-3">INBOX</h6>
                <div className="row">
                  <div className="col-12">
                    {tablelist.map((item, i) => (
                      <div
                        key={item.IndxId ?? i}
                        className={
                          item.ViewStatus !== '2'
                            ? 'mailCard'
                            : 'mailCardUnview'
                        }
                      >
                        <div
                          className="d-flex align-items-center my-2 mx-2 p-2 rounded cursor-pointer"
                          onClick={() => inboxMsg(item)}
                        >
                          <div className="me-3">
                            <div
                              className={`circle ${['first', 'second', 'third', 'fourth', 'fifth'][i % 5]}`}
                            >
                              <b>{item.FrmUsrName?.charAt(0)}</b>
                            </div>
                          </div>
                          <div className="w-100">
                            <div className="d-flex justify-content-between">
                              <span
                                className={
                                  item.ViewStatus !== '2'
                                    ? 'text-body mb-2 fw-bold'
                                    : 'text-body mb-2'
                                }
                              >
                                {item.FrmUsrName}
                              </span>
                              <span
                                className={
                                  item.ViewStatus !== '2'
                                    ? 'tx-11 fw-bold'
                                    : 'text-muted'
                                }
                              >
                                {formatDate(item.DueDate)}
                              </span>
                            </div>
                            <p
                              className={
                                item.ViewStatus !== '2'
                                  ? 'text-muted tx-12 fw-semibold'
                                  : 'text-muted tx-13'
                              }
                            >
                              {item.Title}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Inbox Message Detail */}
          {rightView === 'inboxMsg' && (
            <div className="card dashboard-card inboxMsgCard">
              <div className="card-body">
                <h6 className="mb-3">Inbox</h6>

                {/* Mail header */}
                <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <img
                      src={arrowLeft}
                      alt="back"
                      className="cursor-pointer me-2"
                      onClick={showInboxDashboard}
                    />
                    <span>
                      <b>{mailSubject}</b>
                    </span>
                  </div>
                </div>

                {/* Sender row */}
                <div className="d-flex align-items-center justify-content-between flex-wrap px-3 py-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <span className="text-body">
                      <b>{mailSender}</b>
                    </span>
                    <span className="mx-2 text-muted">to</span>
                    <span className="text-body me-2">me</span>
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    {formatDate(mailReceiveDate)}
                  </div>
                </div>

                {/* Body */}
                <div className="p-4 border-bottom">
                  <p>{mailContent}</p>
                </div>

                {/* Attachments */}
                {attachment1 && (
                  <div className="p-3">
                    <button
                      className="btn-attachment"
                      onClick={() => showAttachments('1')}
                    >
                      <i className="pi pi-paperclip" /> Attachment 1
                    </button>
                  </div>
                )}
                {attachment2 && (
                  <div className="p-3">
                    <button
                      className="btn-attachment"
                      onClick={() => showAttachments('2')}
                    >
                      <i className="pi pi-paperclip" /> Attachment 2
                    </button>
                  </div>
                )}
                {attachment3 && (
                  <div className="p-3">
                    <button
                      className="btn-attachment"
                      onClick={() => showAttachments('3')}
                    >
                      <i className="pi pi-paperclip" /> Attachment 3
                    </button>
                  </div>
                )}

                {/* Task completion form */}
                {showResponseRequire === '1' && (
                  <div className="p-3">
                    <label className="fw-semibold d-block mb-2">
                      Has the task been completed?
                    </label>
                    <div className="task-radio-group">
                      <label>
                        <input
                          type="radio"
                          name="TaskCompleted"
                          value="1"
                          checked={taskCompleted === '1'}
                          onChange={() => setTaskCompleted('1')}
                        />
                        &nbsp;Yes
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="TaskCompleted"
                          value="2"
                          checked={taskCompleted === '2'}
                          onChange={() => setTaskCompleted('2')}
                        />
                        &nbsp;No
                      </label>
                    </div>
                    <button
                      className="btn btn-success mt-2"
                      onClick={saveResponse}
                    >
                      Submit
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Birthday Modal ─────────────────────────────────────────────────── */}
      <Modal
        visible={displayModal}
        title="Today's Birthday"
        onClose={() => setDisplayModal(false)}
        footer={
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setDisplayModal(false)}
          >
            OK
          </button>
        }
      >
        <div className="text-center mb-3">
          <h5 className="fw-bold">Student List</h5>
          <div className="table-responsive">
            <table className="table table-striped birthday-table">
              <thead>
                <tr>
                  <th>EMIS Id</th>
                  <th>Student Name</th>
                  <th>Class &amp; Section</th>
                </tr>
              </thead>
              <tbody>
                {stuBirthdayList.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-muted text-center">
                      No birthdays today
                    </td>
                  </tr>
                ) : (
                  stuBirthdayList.map((p, i) => (
                    <tr key={i}>
                      <td>{p.emis_id}</td>
                      <td>{p.name}</td>
                      <td>{p.details}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="text-center">
          <h5 className="fw-bold">Teacher List</h5>
          <div className="table-responsive">
            <table className="table table-striped birthday-table">
              <thead>
                <tr>
                  <th>EMIS Id</th>
                  <th>Teacher Name</th>
                  <th>Designation</th>
                </tr>
              </thead>
              <tbody>
                {teacherBirthdayList.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-muted text-center">
                      No birthdays today
                    </td>
                  </tr>
                ) : (
                  teacherBirthdayList.map((p, i) => (
                    <tr key={i}>
                      <td>{p.emis_id}</td>
                      <td>{p.name}</td>
                      <td>{p.details}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>

      {/* ── Add Event Modal ────────────────────────────────────────────────── */}
      <Modal
        visible={eventModal}
        title="Add Event"
        onClose={() => setEventModal(false)}
        footer={
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setEventModal(false)}
          >
            Save
          </button>
        }
      >
        <div className="row event-form">
          <div className="col-md-6 col-sm-12 mt-2">
            <label>Venue</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter the Venue"
              value={eventVenue}
              onChange={(e) => setEventVenue(e.target.value)}
            />
          </div>
          <div className="col-md-6 col-sm-12 mt-2">
            <label>Time</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter the Time"
              value={eventTime}
              onChange={(e) => setEventTime(e.target.value)}
            />
          </div>
          <div className="col-md-6 col-sm-12 mt-2">
            <label>Conducted by</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter the Conducted by"
              value={eventConductedBy}
              onChange={(e) => setEventConductedBy(e.target.value)}
            />
          </div>
          <div className="col-md-6 col-sm-12 mt-2">
            <label>Topics Covered</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter the Topics Covered"
              value={eventTopics}
              onChange={(e) => setEventTopics(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </main>
  );
};

export default Dashboard;
